// Keep this calculator in sync with the mobile and Studio copies.
export const HIT_DEGREE_OPTIONS = [0, 30, 45, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];

type KpCusp = number | { longitude?: number };
type KpCusps = Record<number, KpCusp | undefined>;

const normalizeDegree = (degree: number) => ((degree % 360) + 360) % 360;

/** Cusp i starts house i; the next cusp is excluded, including across 0 degrees. */
export function findKpHitHouse(longitude: number, cusps?: KpCusps | null): number | null {
  if (!Number.isFinite(longitude) || !cusps) return null;
  const boundaries: number[] = [];
  for (let house = 1; house <= 12; house++) {
    const cusp = cusps[house];
    const degree = typeof cusp === 'number' ? cusp : cusp?.longitude;
    if (typeof degree !== 'number' || !Number.isFinite(degree)) return null;
    boundaries.push(normalizeDegree(degree));
  }

  // Missing, repeated or out-of-order cusps must not silently produce house 1.
  const spans = boundaries.map((start, index) => normalizeDegree(boundaries[(index + 1) % 12] - start));
  if (spans.some((span) => span === 0) || Math.abs(spans.reduce((sum, span) => sum + span, 0) - 360) > 1e-6) {
    return null;
  }

  const point = normalizeDegree(longitude);
  for (let index = 0; index < 12; index++) {
    const start = boundaries[index];
    const end = boundaries[(index + 1) % 12];
    if (end > start ? point >= start && point < end : point >= start || point < end) {
      return index + 1;
    }
  }
  return null;
}

/** Forward zodiacal projection, independent of shortest-angle aspect matching. */
export function calculateKpHitProjection(longitude: number, selectedDegree: number, cusps?: KpCusps | null) {
  if (!Number.isFinite(longitude) || !Number.isFinite(selectedDegree)) return null;
  const natalLongitude = normalizeDegree(longitude);
  const offset = normalizeDegree(selectedDegree);
  const projectedLongitude = normalizeDegree(natalLongitude + offset);
  return {
    natalLongitude,
    natalHouse: findKpHitHouse(natalLongitude, cusps),
    offset,
    longitude: projectedLongitude,
    house: findKpHitHouse(projectedLongitude, cusps),
  };
}
